/**
 * Pricing Graph Interactive Component
 * Vanilla JavaScript replacement for PricingGraph.vue
 * Features: hover states, scroll animation, dynamic gradient fill
 */

export function initPricingGraph() {
  const graphs = document.querySelectorAll("[data-pricing-graph]");

  graphs.forEach((graph) => {
    const container = graph.querySelector("[data-pricing-container]");
    const graphFill = graph.querySelector("[data-graph-fill]");
    const tiers = graph.querySelectorAll("[data-pricing-tier]");
    const hoverZones = graph.querySelectorAll("[data-hover-zone]");

    if (!container || !graphFill || tiers.length === 0) {
      return; // Missing required elements
    }

    // State
    let activeIndex = 0;
    let inView = false;
    let containerWidth = 0;

    // Calculate widths
    function updateDimensions() {
      containerWidth = container.offsetWidth;
    }

    // Calculate graph fill width based on active tier
    function getGraphWidth(index) {
      const tierCount = tiers.length;
      const columnWidth = (containerWidth - 72) / tierCount;

      if (index === tierCount - 1) {
        return "100%";
      }
      if (index === 0) {
        return "36px";
      }
      return `calc(${columnWidth * index}px + 36px)`;
    }

    // Set active tier
    function setActiveTier(index) {
      if (activeIndex === index) return;

      activeIndex = index;

      // Update all tiers
      tiers.forEach((tier, i) => {
        const isActive = i === index;
        const label = tier.querySelector("[data-tier-label]");
        const text = tier.querySelector("[data-tier-text]");
        const line = tier.querySelector("[data-tier-line]");
        const indicator = line
          ? line.querySelector(".pricing-tier-indicator")
          : null;
        const price = tier.querySelector("[data-tier-price]");
        const priceContent = price
          ? price.querySelector(".pricing-price-content")
          : null;

        // Toggle active class
        tier.classList.toggle("pricing-tier-active", isActive);

        // Label size
        if (label) {
          label.style.fontSize = isActive ? "16px" : "14px";
        }

        // Description text visibility
        if (text) {
          text.style.opacity = isActive ? "1" : "0";
        }

        // Triangle indicator
        if (indicator) {
          indicator.classList.toggle("hidden", !isActive);
        }

        // Price emphasis
        if (priceContent) {
          priceContent.style.fontSize = isActive ? "42px" : "20px";
        }

        // Adjust price sup/sub positioning when active
        if (isActive && price) {
          const sup = price.querySelector("sup");
          const sub = price.querySelector("sub");
          if (sup) sup.style.verticalAlign = "super";
          if (sub) sub.style.verticalAlign = "baseline";
        } else if (price) {
          const sup = price.querySelector("sup");
          const sub = price.querySelector("sub");
          if (sup) sup.style.verticalAlign = "middle";
          if (sub) sub.style.verticalAlign = "middle";
        }
      });

      // Update graph fill width
      updateDimensions();
      graphFill.style.width = getGraphWidth(index);

      // Recalculate line heights after DOM updates (text visibility changes)
      requestAnimationFrame(() => {
        updateLineHeights();
      });
    }

    // Calculate line height for each tier
    function updateLineHeights() {
      tiers.forEach((tier, index) => {
        const label = tier.querySelector("[data-tier-label]");
        const text = tier.querySelector("[data-tier-text]");
        const line = tier.querySelector("[data-tier-line]");
        const isActive = index === activeIndex;

        if (line) {
          const tierHeight = tier.offsetHeight;
          const labelHeight = label ? label.offsetHeight : 0;
          const textHeight = isActive && text ? text.offsetHeight : 0;

          // Line extends from bottom to just below the content (label + text when active)
          // Accounting for spacing (mt-2 = 8px on bar, plus additional spacing)
          const lineHeight = tierHeight - labelHeight - textHeight - 10;
          line.style.height = `${Math.max(lineHeight, 20)}px`; // minimum 20px
        }
      });
    }

    // Hover handlers
    hoverZones.forEach((zone, index) => {
      zone.addEventListener("mouseenter", () => {
        if (inView) {
          setActiveTier(index);
        }
      });
    });

    // Price hover handlers
    tiers.forEach((tier, index) => {
      const priceElement = tier.querySelector("[data-tier-price]");
      if (priceElement) {
        priceElement.addEventListener("mouseenter", () => {
          if (inView) {
            setActiveTier(index);
          }
        });
      }
    });

    // Staggered reveal animation
    function animateGraphReveal() {
      tiers.forEach((tier, index) => {
        const bar = tier.querySelector("[data-tier-bar]");
        const price = tier.querySelector("[data-tier-price]");
        const staggerDelay = index * 100; // 100ms between each tier

        // Tier container fade in + slide up
        setTimeout(() => {
          tier.classList.remove("opacity-0", "translate-y-8");
          tier.classList.add("opacity-100", "translate-y-0");
          tier.setAttribute("data-animate-in", "true");
        }, staggerDelay);

        // Bar scale animation (slightly delayed after container)
        if (bar) {
          setTimeout(() => {
            bar.classList.remove("scale-y-0");
            bar.classList.add("scale-y-100");
          }, staggerDelay + 200);
        }

        // Price fade in (after bar animation)
        if (price) {
          setTimeout(() => {
            price.classList.remove("opacity-0");
            price.classList.add("opacity-100");
          }, staggerDelay + 700);
        }
      });
    }

    // Scroll animation using IntersectionObserver
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && !inView) {
            // Trigger staggered reveal animation
            animateGraphReveal();

            // After reveal completes, calculate initial line heights and animate to last tier
            setTimeout(() => {
              updateLineHeights();
              setActiveTier(tiers.length - 1);
              setTimeout(() => {
                inView = true;
              }, 1000);
            }, 1400);
          }
        });
      },
      {
        threshold: 0.5,
        rootMargin: "0px",
      }
    );

    observer.observe(container);

    // Window resize handler
    function handleResize() {
      updateDimensions();
      updateLineHeights();
      graphFill.style.width = getGraphWidth(activeIndex);
    }

    window.addEventListener("resize", handleResize);

    // Initial setup
    updateDimensions();
    updateLineHeights();
    setActiveTier(0);

    // Cleanup function (if needed for SPA-like behavior)
    graph._cleanup = () => {
      observer.disconnect();
      window.removeEventListener("resize", handleResize);
    };
  });
}

// Auto-initialize on DOM ready
if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", initPricingGraph);
} else {
  initPricingGraph();
}
